import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const IMPERSONATION_KEY = "impersonation_admin_user_id";

interface ImpersonationState {
  adminUserId: string;
  targetEmail: string;
  sessionToken: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, companyName: string, firstName?: string, lastName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  impersonation: ImpersonationState | null;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const restoringImpersonationRef = useRef(false);
  const [impersonation, setImpersonationState] = useState<ImpersonationState | null>(() => {
    try {
      const stored = sessionStorage.getItem(IMPERSONATION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setImpersonation = (state: ImpersonationState | null) => {
    setImpersonationState(state);
    if (state) {
      sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(IMPERSONATION_KEY);
    }
  };

  // Clear stale impersonation state if we're already logged in as the admin
  useEffect(() => {
    if (user && impersonation && user.id === impersonation.adminUserId) {
      setImpersonation(null);
    }
  }, [user, impersonation]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Process pending team invite after login/signup confirmation
        if (event === 'SIGNED_IN' && session?.user) {
          // Link any anonymous attribution touches captured before login to this user
          try {
            const { linkAttributionToUser } = await import("@/lib/attribution");
            void linkAttributionToUser(session.user.id);
          } catch (e) {
            console.error("Attribution link failed:", e);
          }
          try {
            const raw = sessionStorage.getItem("pending_invite");
            if (raw) {
              sessionStorage.removeItem("pending_invite");
              const invite = JSON.parse(raw);
              await supabase.from("company_members").insert({
                company_id: invite.companyId,
                user_id: session.user.id,
                role: "member",
              });
              await supabase
                .from("company_invitations")
                .update({ status: "accepted" })
                .eq("id", invite.invitationId);
            }
          } catch (e) {
            console.error("Failed to process pending invite:", e);
          }
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, companyName: string, firstName?: string, lastName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: {
          full_name: fullName,
          first_name: firstName || '',
          last_name: lastName || '',
          company_name: companyName,
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    setImpersonation(null);
    await supabase.auth.signOut();
  };

  const invokeCreateAdminUser = useCallback(async (body: Record<string, unknown>) => {
    const { data: { session: activeSession }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !activeSession?.access_token) {
      throw new Error("Sessie verlopen. Log opnieuw in.");
    }

    const res = await supabase.functions.invoke("create-admin-user", {
      body,
      headers: { Authorization: `Bearer ${activeSession.access_token}` },
    });

    if (res.error) throw res.error;
    if (res.data?.error) throw new Error(res.data.error);
    return res.data;
  }, []);

  const impersonate = useCallback(async (userId: string) => {
    if (!session || !user) throw new Error("Niet ingelogd");

    const data = await invokeCreateAdminUser({ action: "login-as", userId });

    const { tokenHash, email, sessionToken } = data;
    if (!tokenHash) throw new Error("Geen token ontvangen");
    if (!sessionToken) throw new Error("Geen sessie token ontvangen");

    // Store admin user ID before switching
    const adminUserId = user.id;

    const { error: otpError } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: "magiclink",
    });
    if (otpError) throw otpError;

    const impState: ImpersonationState = { adminUserId, targetEmail: email, sessionToken };
    setImpersonation(impState);
  }, [session, user, invokeCreateAdminUser]);

  const stopImpersonation = useCallback(async () => {
    if (!impersonation) return;
    if (restoringImpersonationRef.current) return;
    restoringImpersonationRef.current = true;

    const { adminUserId, sessionToken } = impersonation;
    setImpersonation(null);

    try {
      const data = await invokeCreateAdminUser({ action: "restore-admin", adminUserId, sessionToken });
      const { tokenHash } = data;
      if (!tokenHash) throw new Error("Geen token ontvangen");

      // Sign in as admin using the magic link
      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: "magiclink",
      });

      if (otpError) {
        await supabase.auth.signOut();
      }
    } catch (err) {
      await supabase.auth.signOut();
    } finally {
      restoringImpersonationRef.current = false;
    }
  }, [impersonation, invokeCreateAdminUser]);

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut, impersonation, impersonate, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};
